/**
 * Tờ thu tiền — phần "khách trả tại quầy bằng gì" (KH-GĐ2 5.1, docs/plans/gd2-5-tien-dung.md):
 *   - một cách (như cũ) hoặc "Khách trả nhiều cách": ô số cho tiền mặt / chuyển khoản / thẻ, báo còn thiếu / thừa;
 *   - chuyển khoản → "Hiện mã QR" (máy chủ dựng chuỗi VietQR vào tài khoản của tiệm, nội dung "TT <mã lịch>").
 * Máy chủ tự tính phần quầy phải thu và bắt tổng các phần khớp đúng (lệch → 409, không ghi gì).
 */

import { useEffect, useState } from 'react'
import { renderSVG } from 'uqr'
import { type Api, ApiFailure, type HinhThucThu, tienVnd } from './api.ts'
import { CAC_CACH, CHIA_RONG, type ChiaTien, chiaHopLe, tongChia } from './chia-tra.ts'
import { ChonHinhThuc, OTien } from './GoiVi.tsx'
import { QrChuaCoTaiKhoan } from './QrChuaCoTaiKhoan.tsx'
import { G } from './text/goi-vi.ts'
import { T } from './text/vi.ts'
import { ThongBao } from './ui.tsx'

export { CHIA_RONG, cacPhanChia, chiaHopLe } from './chia-tra.ts'
export type { ChiaTien }

type Qr = Awaited<ReturnType<Api['qrThu']>>

export function PhanTraTaiQuay({
  api,
  appointmentId,
  phaiThu,
  hinhThuc,
  onDoiHinhThuc,
  chia,
  onDoiChia,
  onKhaiTaiKhoan,
}: {
  api: Api
  appointmentId: string
  /** Phần quầy phải thu (sau giảm, cọc, ví) — cùng công thức với máy chủ. */
  phaiThu: bigint
  hinhThuc: HinhThucThu
  onDoiHinhThuc: (h: HinhThucThu) => void
  /** `null` = một cách; có giá trị = đang chia nhiều cách. */
  chia: ChiaTien | null
  onDoiChia: (c: ChiaTien | null) => void
  /** Chỉ CHỦ tiệm có: tiệm chưa khai tài khoản nhận tiền thì hiện nút dẫn tới thẻ khai (v0.23.2). Vai khác: câu nhờ chủ, không nút. */
  onKhaiTaiKhoan?: () => void
}) {
  const [qr, setQr] = useState<Qr | null>(null)
  const [dangQr, setDangQr] = useState(false)
  const [loiQr, setLoiQr] = useState<string | null>(null)
  // Phần phải thu đổi (thêm sản phẩm, giảm giá…) → QR cũ mang số cũ: bỏ ngay, bắt tạo lại (money-auditor 5.1 NẶNG 1).
  // biome-ignore lint/correctness/useExhaustiveDependencies: chạy lại đúng khi số phải thu đổi.
  useEffect(() => {
    setQr(null)
  }, [phaiThu])

  const soChuyenKhoan =
    chia === null
      ? hinhThuc === 'transfer'
        ? phaiThu
        : 0n
      : chia.transfer === ''
        ? 0n
        : BigInt(chia.transfer)
  const tong = chia === null ? phaiThu : tongChia(chia)
  const lech = phaiThu - tong

  const svgQr =
    qr === null || qr.qr === null
      ? ''
      : renderSVG(qr.qr, { ecc: 'M', border: 2, blackColor: '#1f1a1c', whiteColor: '#ffffff' })

  async function hienQr() {
    setDangQr(true)
    setLoiQr(null)
    try {
      setQr(await api.qrThu(appointmentId, soChuyenKhoan.toString()))
    } catch (e) {
      setLoiQr(e instanceof ApiFailure ? e.messageVi : T.serverFailed)
    } finally {
      setDangQr(false)
    }
  }

  return (
    <>
      {chia === null ? (
        <ChonHinhThuc
          giaTri={hinhThuc}
          onDoi={(h) => {
            onDoiHinhThuc(h)
            setQr(null)
          }}
          nhan={G.counterPayBy}
        />
      ) : (
        <div className="muc-to">
          <h3>{G.splitTitle}</h3>
          <div className="luoi-chia">
            {CAC_CACH.map((h) => (
              <label key={h} className="truong" htmlFor={`chia-${h}`}>
                <span className="nhan">{G.hinhThuc[h]}</span>
                <OTien
                  id={`chia-${h}`}
                  giaTri={chia[h]}
                  onDoi={(so) => {
                    onDoiChia({ ...chia, [h]: so })
                    setQr(null)
                  }}
                />
              </label>
            ))}
          </div>
          <p className={`chu-phu ${lech === 0n ? 'du-tien' : 'thieu-tien'}`}>
            {lech === 0n
              ? G.splitOk
              : lech > 0n
                ? G.splitShort(tienVnd(lech.toString()))
                : G.splitOver(tienVnd((-lech).toString()))}
          </p>
        </div>
      )}
      <button
        type="button"
        className="nut-lien-ket"
        onClick={() => {
          onDoiChia(chia === null ? { ...CHIA_RONG, [hinhThuc]: phaiThu.toString() } : null)
          setQr(null)
        }}
      >
        {chia === null ? G.splitOpen : G.splitClose}
      </button>

      {/* Chỉ hiện QR khi số đã khớp số phải thu — không để khách quét một số đang gõ dở / gõ nhầm. */}
      {soChuyenKhoan > 0n &&
        soChuyenKhoan % 1000n === 0n &&
        (chia === null || chiaHopLe(chia, phaiThu)) && (
          <div className="muc-to qr-thu">
            {qr === null ? (
              <button
                type="button"
                className="nut nut-vien"
                disabled={dangQr}
                onClick={() => void hienQr()}
              >
                {G.showQr(tienVnd(soChuyenKhoan.toString()))}
              </button>
            ) : qr.qr === null ? (
              <QrChuaCoTaiKhoan
                {...(onKhaiTaiKhoan === undefined ? {} : { onKhai: onKhaiTaiKhoan })}
              />
            ) : (
              <div className="qr-thu-khung">
                {/* biome-ignore lint/security/noDangerouslySetInnerHtml: SVG do uqr tạo từ chuỗi VietQR máy chủ dựng, không có dữ liệu người dùng nhập. */}
                <div className="qr-thu-ma" dangerouslySetInnerHTML={{ __html: svgQr }} />
                <p>
                  <b>{tienVnd(qr.soTienVnd ?? '0')}</b>
                </p>
                <p className="chu-phu">
                  {qr.nganHang ?? ''} · {qr.soTaiKhoan} · {qr.tenTaiKhoan}
                </p>
                <p className="chu-phu">{G.qrContent(qr.noiDung ?? '')}</p>
              </div>
            )}
            {loiQr !== null && <ThongBao loai="loi">{loiQr}</ThongBao>}
          </div>
        )}
    </>
  )
}
